import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import InitialSetupPage from '@/pages/InitialSetupPage';
import StaffHomePage from '@/pages/StaffHomePage';
import ReferralDoctorsPage from '@/pages/ReferralDoctorsPage';
import InhouseConsultantsPage from '@/pages/InhouseConsultantsPage';
import ReferralEntryPage from '@/pages/ReferralEntryPage';
import ReferralsListPage from '@/pages/ReferralsListPage';
import ReportsPage from '@/pages/ReportsPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import AdminRecoveryPage from '@/pages/AdminRecoveryPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import ManagerCalculationsPage from '@/pages/ManagerCalculationsPage';
import MarketingDashboardPage from '@/pages/MarketingDashboardPage';
import '@/App.css';

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-slate-950 text-slate-100 antialiased selection:bg-teal-500 selection:text-white">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<InitialSetupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/admin-recovery" element={<AdminRecoveryPage />} />
            <Route 
              path="/profile/change-password" 
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              } 
            />

            {/* Protected Staff Routes */}
            <Route 
              path="/staff/home" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'staff', 'accountant', 'reception', 'lab_staff', 'pharmacist', 'management', 'manager']}>
                  <StaffHomePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff/referrals/new" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'staff', 'reception']}>
                  <ReferralEntryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff/referrals/:referralId/edit" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'lab_staff', 'pharmacist', 'reception']}>
                  <ReferralEntryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff/referrals" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'staff', 'accountant', 'reception', 'lab_staff', 'pharmacist', 'management', 'manager']}>
                  <ReferralsListPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/reports" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                  <ReportsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'management', 'manager']}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/calculations" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'manager', 'management']}>
                  <ManagerCalculationsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/marketing/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'marketing_executive']}>
                  <MarketingDashboardPage />
                </ProtectedRoute>
              } 
            />

            {/* Admin-only Routes */}
            <Route 
              path="/admin/referral-doctors" 
              element={
                <ProtectedRoute requireAdmin>
                  <ReferralDoctorsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/consultants" 
              element={
                <ProtectedRoute requireAdmin>
                  <InhouseConsultantsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/marketing-executives" 
              element={<Navigate to="/staff/home" replace />}
            />

            {/* Default root redirect */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <StaffHomePage />
                </ProtectedRoute>
              } 
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
